import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CustomTooltip from "components/CustomTooltip/CustomTooltip";
import "./styles.css";

const ViewActionModal = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "",
  showInput = false,
  inputLabel = "Input",
  inputPlaceholder = "",
  inputValue = "",
  onInputChange,
  showCheckbox = false,
  checkboxLabel = "",
  checkboxValue = false,
  onCheckboxChange,
  showCheckboxTooltip = false,
  checkboxTooltipText = "",
  cancelButtonText = "Cancel",
  confirmButtonText = "Confirm",
  confirmButtonColor = "primary",
  maxWidth = "sm",
  fullWidth = true,
}) => {
  const [localInputValue, setLocalInputValue] = useState(inputValue);
  const [localCheckboxValue, setLocalCheckboxValue] = useState(checkboxValue);

  // Sync local state with prop value when modal opens
  useEffect(() => {
    if (open) {
      setLocalInputValue(inputValue);
      setLocalCheckboxValue(checkboxValue);
    }
  }, [open, inputValue, checkboxValue]);

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setLocalInputValue(newValue);
    onInputChange?.(newValue);
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const newValue = e.target.checked;
    setLocalCheckboxValue(newValue);
    onCheckboxChange?.(newValue);
  };

  // Handle confirm action
  const handleConfirm = () => {
    if (showInput && showCheckbox) {
      onConfirm?.(localInputValue, localCheckboxValue);
    } else if (showInput) {
      onConfirm?.(localInputValue);
    } else if (showCheckbox) {
      onConfirm?.(localCheckboxValue);
    } else {
      onConfirm?.();
    }
  };

  // Handle close/cancel action
  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      className="view-action-modal"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {/* CHANGED per explicit request ("Title should not be bold. It
              should be regular.") after a fresh get_design_context on this
              exact popup's own Figma node (10137:322529's "dialogDeleteComment",
              Title child 27077:58182): type/message/emphasized is 20px/400/
              -0.1px/28px - already what this sx stated, but every value here
              is now suffixed " !important" since the plain (non-!important)
              values were losing to this app's own global MuiTypography/
              MuiDialogTitle theme overrides (this file was the one modal
              still styled through an external stylesheet - see styles.css -
              rather than the sx-only approach used on every other modal
              fixed this session, which is what let a global override win
              here specifically). */}
          <Typography component="span" sx={{
            color: '#01151D !important',
            fontFamily: '"Source Sans Pro", sans-serif',
            fontSize: '20px !important',
            fontWeight: '400 !important',
            lineHeight: '28px !important',
            letterSpacing: '-0.1px !important'
          }}>
            {title}
          </Typography>
          <IconButton
            onClick={handleClose}
            className="view-action-modal-close-btn"
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Box sx={{ px: '24px'}}>
        {/* CHANGED per explicit request ("Description font size should be
            16px"): confirmed via the same fresh get_design_context (node
            10137:322529's dialogContent, 5846:333478) as type/body - 16px/
            400/24px line-height/-0.04px letter-spacing/color
            var(--text/base/default,#01151d), not the plain black (#000)
            this Typography's own className (styles.css) had been using.
            The previous inline sx here also had a bug independent of the
            reported issue - lineHeight:'1' would have crushed the message
            to a single collapsed line if the external stylesheet's
            line-height:24px!important hadn't been masking it - replaced
            with the correct value stated directly, same "own every value
            via sx, don't depend on the external stylesheet" fix as the
            title above. */}
        {message && (
          <Typography variant="body2" className="view-action-modal-message" sx={{
            color: '#01151D !important',
            fontFamily: '"Source Sans Pro", sans-serif',
            fontSize: '16px !important',
            fontWeight: '400 !important',
            lineHeight: '24px !important',
            letterSpacing: '-0.04px !important',
            marginBottom: '16px'
          }}>
            {message}
          </Typography>
        )}
        {showInput && (
          <TextField
            fullWidth
            size="small"
            label={inputLabel}
            placeholder={inputPlaceholder}
            value={localInputValue}
            onChange={handleInputChange}
            variant="outlined"
            autoFocus
          />
        )}
        {showCheckbox && (
          <FormControlLabel
            control={
              <Checkbox
                checked={localCheckboxValue}
                onChange={handleCheckboxChange}
                size="small"
                sx={{ 
                  color: '#6B7280',
                  '&.Mui-checked': {
                    color: '#0977A0',
                  }
                }}
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography sx={{ fontSize: "14px", color: "#374151" }}>
                  {checkboxLabel}
                </Typography>
                {showCheckboxTooltip && (
                  <CustomTooltip
                    title={checkboxTooltipText}
                    arrow
                    placement="top"
                    enterDelay={300}
                    leaveDelay={200}
                    maxWidth="227px"
                  >
                    <InfoOutlinedIcon
                      sx={{ fontSize: '16px', color: '#637177', cursor: 'pointer' }}
                    />
                  </CustomTooltip>
                )}
              </Box>
            }
            sx={{ mt: showInput ? 2 : 0 }}
          />
        )}
      </Box>
      <DialogActions sx={{ px: '24px', pb: '24px' }}>
        {/* CHANGED per explicit request ("Cancel button is still in legacy
            design"): the fresh get_design_context on this popup (node
            10137:322529's footer) shows Cancel as Figma's "Button/Secondary"
            (hierarchy="Secondary", size="Large") - the same Solstice
            Secondary treatment already established and applied this session
            to the Upload modal's and Edit-document modal's own Cancel
            buttons (rgba(62,119,139,0.16) bg / rgba(62,119,139,0.22) hover /
            #01151D text / no border). This component's own
            view-action-modal-cancel-btn className (styles.css) had instead
            been left on an older, different teal tint
            (rgba(30,105,118,0.08)) predating that established recipe - that
            mismatched shade is the "legacy design" being reported. Styled
            directly via sx now (in addition to updating the className's
            colors below) for the same "don't depend solely on the external
            stylesheet" reason as the title/message above. */}
        {/* size/padding ADDED per explicit request ("Delete button size and
            padding are not consistent as Figma design. Font size should be
            16px."): Figma's Destructive/Delete button (node
            …19240:80130) is comp/button/large - 44px height, 16px
            horizontal padding, 6px radius, 16px/400/24px/-0.04px text
            (type/body, same as Cancel/every other 44px button fixed this
            session) - this shared .MuiButton-root rule in styles.css
            already stated those same numbers but without !important on
            most of them, so this app's own global MuiButton theme override
            (injected after this plain stylesheet) was winning and
            overriding them at render time. Both buttons get the same base
            sizing via sx now so neither depends on that cascade order. */}
        <Button
          onClick={handleClose}
          className="view-action-modal-cancel-btn"
          sx={{
            height: '44px',
            minHeight: '44px',
            padding: '0 16px',
            borderRadius: '6px !important',
            textTransform: 'none',
            fontFamily: '"Source Sans Pro", sans-serif',
            fontSize: '16px !important',
            fontWeight: '400 !important',
            lineHeight: '24px !important',
            letterSpacing: '-0.04px',
            color: '#01151D !important',
            backgroundColor: 'rgba(62, 119, 139, 0.16) !important',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(62, 119, 139, 0.22) !important',
              boxShadow: 'none',
            },
          }}
        >
          {cancelButtonText}
        </Button>
        <Button
          variant="contained"
          color={confirmButtonColor}
          onClick={handleConfirm}
          className="view-action-modal-confirm-btn"
          sx={{
            height: '44px',
            minHeight: '44px',
            padding: '0 16px',
            borderRadius: '6px !important',
            textTransform: 'none',
            fontFamily: '"Source Sans Pro", sans-serif',
            fontSize: '16px !important',
            fontWeight: '400 !important',
            lineHeight: '24px !important',
            letterSpacing: '-0.04px',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {confirmButtonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(ViewActionModal);
